import {zodResolver} from '@mantine/form';
import {useDisclosure} from '@mantine/hooks';
import type {AppError} from '@packages/utils/src';
import {z} from 'zod';
import {PROFILE_BASE} from '../../../config/constants/routes';
import {DevelopmentExperienceSchema} from '../../../core/entities/mypage/schema';
import {GROUPED_HASHTAGS_KEY} from '../../../core/entities/register/constants';
import {
  type PutItSkillsDataType,
  useDevelopmentExperiences,
  usePostDevelopmentExperiences,
} from '../../../core/repositories/mypageRepository';
import {
  showErrorSnackBar,
  showSuccessSnackBar,
} from '../../../shared/components/SnackBar';
import {useIsAppWebView} from '../../../shared/hooks/useIsAppWebView';
import {useHashtagData} from '../../../shared/services/useHashtagData';
import {
  type DevelopmentExperiencesValues,
  useDevelopmentExperiencesForm,
} from '../context/DevelopExperiencesContext';

type Props = {
  onPutSuccessFunc?: () => void;
};

type CastedError = {message: AppError};

const validateSchema = z.object({
  experiences: z.array(DevelopmentExperienceSchema),
});

export const useHandleDevelopmentExperiences = ({
  onPutSuccessFunc = undefined,
}: Props) => {
  const isWebView = useIsAppWebView();
  const developmentHashtagCategoryID = 12;
  const categories = useHashtagData(
    developmentHashtagCategoryID,
    GROUPED_HASHTAGS_KEY.DEV_EXP_CATEGORY,
    10,
  );
  const languages = useHashtagData(
    developmentHashtagCategoryID,
    GROUPED_HASHTAGS_KEY.PROGRAMMING_LANGUAGE,
    100,
  );
  const frameworks = useHashtagData(
    developmentHashtagCategoryID,
    GROUPED_HASHTAGS_KEY.LIBRARY_FRAMEWORK,
    100,
  );
  const others = useHashtagData(
    developmentHashtagCategoryID,
    GROUPED_HASHTAGS_KEY.DATABASE,
    100,
  );

  const {developmentExperiencesData, refetch} = useDevelopmentExperiences({});

  const experiencesLength = developmentExperiencesData?.length ?? 0;

  const form = useDevelopmentExperiencesForm({
    validate: zodResolver(validateSchema),
    initialValues: {
      experiences: [
        {
          id: undefined,
          categoryTag: {hashtagID: 0, hashtagName: ''},
          body: '',
          startDate: '',
          endDate: '',
          programmingLanguageTags: [],
          libraryFrameworkTags: [],
          otherTags: [],
        },
      ],
    },
  });

  const saveDevelopmentExperiences = usePostDevelopmentExperiences({
    onSuccessFnc() {
      handlers.close();
      showSuccessSnackBar({title: '保存しました', message: 'done'});
      refetch();
      if (onPutSuccessFunc) {
        onPutSuccessFunc();
      }

      setTimeout(() => {
        if (isWebView) {
          location.href = PROFILE_BASE;
        } else {
          window.open(PROFILE_BASE);
        }
      }, 1500);
    },
    onErrorFnc(err: unknown) {
      const castedError = err as CastedError;
      handlers.close();
      showErrorSnackBar({
        isNetworkError: castedError.message === 'NetworkError',
      });
    },
  });

  const [_isLoadingPut, handlers] = useDisclosure(false);

  const transformValues = (
    values: DevelopmentExperiencesValues,
  ): PutItSkillsDataType['experiences'] => {
    const transformedExperiences = values.experiences.map((experience) => ({
      id: experience.id,
      body: experience.body,
      categoryTagID: experience.categoryTag.hashtagID,
      startYear: experience.startDate
        ? new Date(experience.startDate).getFullYear()
        : undefined,
      startMonth: experience.startDate
        ? new Date(experience.startDate).getMonth() + 1
        : undefined,
      endYear: experience.endDate
        ? new Date(experience.endDate).getFullYear()
        : undefined,
      endMonth: experience.endDate
        ? new Date(experience.endDate).getMonth() + 1
        : undefined,
      hashtagIDs: [
        ...(experience.programmingLanguageTags?.map(
          (value) => value.hashtagID,
        ) ?? []),
        ...(experience.libraryFrameworkTags?.map((value) => value.hashtagID) ??
          []),
        ...(experience.otherTags?.map((value) => value.hashtagID) ?? []),
      ],
    }));

    return transformedExperiences;
  };

  const submitPut = (values: PutItSkillsDataType['experiences']) => {
    saveDevelopmentExperiences.mutate(values);
  };

  return {
    form,
    experiencesLength,
    submitPut,
    transformValues,
    categories,
    languages,
    frameworks,
    others,
  };
};
